import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { HeroFigure } from '@/components/dashboard/HeroFigure'
import { Badge, Button, Panel, SectionLabel, Stat } from '@/components/ui'
import { MiniLineChart } from '@/components/dashboard/MiniLineChart'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import distributorAbi from '@/lib/abis/HorizonRevenueDistributor.json'
import { fmt, fmtBalance, fmtUsd, toNumber, truncAddr, POLL_SLOW } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { useProtocolState } from '@/hooks/useProtocolState'
import { useVeNFTs } from '@/hooks/useVeNFTs'
import { useViewedAddress } from '@/hooks/useViewedAddress'
import { usePointsData } from '@/hooks/usePointsData'
import { useLiveAccrual } from '@/hooks/useLiveAccrual'
import { useUserPortfolioHistory } from '@/hooks/useUserPortfolioHistory'
import { useTotalClaimedHorizon } from '@/hooks/useTotalClaimedHorizon'
import { useVaultsList } from '@/hooks/useVaultsList'
import { useVaultEmissions } from '@/hooks/useVaultEmissions'
import { useRevenueShare } from '@/hooks/useRevenueShare'
import { shareOnX } from '@/lib/shareStats'

const USDX_ADDR = (ADDRESSES.USDX || '').toLowerCase()
const HORIZON_ADDR = (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()

/// Everything the connected wallet holds across the whole protocol, in one place. Total Portfolio
/// Value covers the money market net, staked HORIZON, the HORIZON wallet balance, and ALM vault
/// holdings. The vault leg replaced an LP-farm leg when HORIZON/USDx became a vault like any other,
/// and it counts MORE than the farm version did: every vault the wallet holds, not just one pair.
///
/// A vault whose pair has no USD reference on either side has its unpriced side valued at $0
/// rather than guessed - `fullyPriced: false` from useVaultsList surfaces that as a "≥" prefix on
/// the row itself, never silently folded into the total.
///
/// HORIZON Incentives (the money market's per-reserve supply/borrow streams, plus vault-share
/// emissions) claims from here now, not from each product page - those pages still show
/// their own pending HORIZON ticking live (so the number means something in context), but actually
/// claiming it is one action in one place instead of the same "go find the claim button on each
/// product page" hunt repeated three times. Staking's claim stays on Stake itself: it's bundled
/// with the stake/unstake decision (claim vs. compound), not a passive accrual stream like the
/// other three, so it doesn't belong in this consolidation.
export function PortfolioSection() {
  const { address: connected } = useAccount()
  // Portfolio shows the CONNECTED wallet by default. A `?view=0x...` URL param (a Leaderboard row
  // deep-links to one) shows that address READ-ONLY. The manual "watch / add address" list was
  // removed (founder call), so viewing another address is deep-link only now. `user` stays the name
  // every read/hook below keys off.
  const { viewAddress, isViewingOther } = useViewedAddress(connected)
  const user = viewAddress
  const { rate: horizonUsd, found: hasPrice } = useHorizonPriceUsd()
  const { d, refetch: refetchProtocol } = useProtocolState(viewAddress)
  // Per-user locked HORIZON has no aggregate getter in the veNFT model, so sum the holder's positions.
  const { positions: vePositions } = useVeNFTs(user)
  const userLockedWei = useMemo(() => vePositions.reduce((a, p) => a + (p.amount ?? 0n), 0n), [vePositions])
  const userLocked = toNumber(userLockedWei)

  // Entry 182: staking merged into the lock - the committed-HORIZON figure is the ve lock amount,
  // which accrues revenue share (shown in the revenue panel) rather than a HORIZON-APY stream.

  // Also above the `!user` early return, same reason as the useLiveAccrual call above.
  const { series: historySeries, loading: historyLoading } = useUserPortfolioHistory(user)
  // These two used to live below the early return - safe only as long as `user` never flipped
  // from falsy to truthy without a full remount in between. That stopped being safe once Portfolio
  // could switch to viewing a freshly-added watched address mid-session (a real "Rendered more
  // hooks than during the previous render" crash, caught by manual testing) - moved up here to
  // match the two hooks above, which were already correctly placed for the same reason.
  const { totalClaimedHorizon: totalEarnedHorizon } = useTotalClaimedHorizon(user)
  // Placed with the other unconditional hooks above, NOT inside any branch - this file has already
  // been bitten once by a conditional hook call (CHANGELOG.md item 57).
  const { vaults: allVaults } = useVaultsList();
  const vaultEmissions = useVaultEmissions(allVaults, user);
  const { pendingByAsset: pendingRevenueByAsset, refetchPending: refetchRevenueShare } = useRevenueShare(user)
  // Also above the `!user` early return, same reason as every other viewAddress-keyed hook here -
  // a real bug this exact page already hit once (see the git history around CHANGELOG.md item 55's
  // watch-address feature): a hook called only below an early return breaks React's rules of
  // hooks the moment `user` flips from falsy to truthy mid-session, which is a completely normal
  // path here (switching the AddressSwitcher from "nothing selected" to a real address).

  const usdx = d.reserves.find((r) => r.key === 'usdx')
  const eth = d.reserves.find((r) => r.key === 'eth')

  if (!user) {
    return (
      <div id="portfolio">
        <SectionHeader title="Portfolio" subtitle="Your complete position across the platform." />
        <div style={{
          padding: '56px 24px', textAlign: 'center',
          background: T.glassSurface, backdropFilter: T.glassBlur, WebkitBackdropFilter: T.glassBlur,
          border: `1px solid ${T.border}`, borderRadius: T.r.lg,
        }}>
          <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>
            Connect your wallet to view your portfolio.
          </div>
          <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>
            Your positions, rewards, and points appear here once connected.
          </div>
          {/* The one action this whole page depends on, right here rather than only in the far-away
              top-right nav. */}
          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
            <ConnectButton />
          </div>
        </div>
      </div>
    )
  }

  const horizonBalance = toNumber(d.userHorizonBalWei)

  const horizonValueUsd = hasPrice ? horizonBalance * horizonUsd : 0
  const stakedHorizonValueUsd = hasPrice ? userLocked * horizonUsd : 0
  // usdx may not be a listed reserve yet (staged launch: the pool can be live with zero or a
  // different reserve set), so treat a missing reserve as no position rather than assuming it exists.
  const earnValueUsd = usdx ? usdx.user.supplyBalance * usdx.priceUsd : 0
  // Net value across BOTH reserves (was ETH-collateral-minus-USDx-debt only, back when Market A
  // was the only place with a "collateral vs debt" position) - a symmetric pool can hold supply
  // and debt in either reserve, so this sums raw supplied value minus raw debt value across the
  // whole account, not the LTV-weighted `accountTotalCollateralUsd` the Borrow page's own
  // available-to-borrow math uses (that figure is deliberately discounted by each reserve's
  // collateral factor - a "how much can I still borrow" number, not a "what do I actually have"
  // number, which is what a net-worth figure needs).
  const totalSuppliedUsd = d.reserves.reduce((s, r) => s + r.user.supplyBalance * r.priceUsd, 0)
  const totalDebtUsd = d.reserves.reduce((s, r) => s + r.user.debt * r.priceUsd, 0)
  const borrowNetUsd = totalSuppliedUsd - totalDebtUsd
  // ALM vault holdings, valued by useVaultsList's own TWAP-derived pricing. This line used to sum
  // LP farm shares instead; the farm is gone and its one pair is a vault now, so the same capital
  // is counted here - and every OTHER vault the wallet holds, which the farm-only version missed.
  const vaultValueUsd = allVaults.reduce((s, v) => s + (v.userTvlUsd || 0), 0)
  const totalPortfolioUsd = horizonValueUsd + stakedHorizonValueUsd + borrowNetUsd + vaultValueUsd

  const heldVaults = allVaults.filter((v) => v.hasPosition)
  const hasVaultPosition = heldVaults.length > 0
  const hasMoneyMarketPosition = d.reserves.some((r) => r.user.supplyBalance > 0 || r.user.debt > 0)
  // A wallet holding nothing across the whole protocol - drives the first-run "get started" panel
  // instead of a page of $0.00s. (Points/referrals are handled by their own panel's empty state.)
  const isEmptyPortfolio = totalPortfolioUsd === 0 && !hasVaultPosition && !hasMoneyMarketPosition && userLocked === 0 && horizonBalance === 0

  // "Proof of profitability" figure for the hero stat and ShareCard - HORIZON actually claimed to
  // this wallet, ever, across every reward stream (farm, both reserves' supply + borrow
  // incentives, staking), not the currently-pending/unclaimed amount (that's shown separately,
  // per-stream, elsewhere in this panel - e.g. "Staked HORIZON... +pending" below, and each
  // reserve's own pending Supply/Borrow HORIZON rows in PositionPanel). Reconstructed from claim
  // event logs since none of these contracts track a running claimed total on-chain - see
  // useTotalClaimedHorizon's own doc comment. USD equivalent uses the same hasPrice/horizonUsd this
  // component already reads. (Hook call itself now lives above the early return - see that
  // comment for why.)
  const totalEarnedUsd = hasPrice ? totalEarnedHorizon * horizonUsd : 0

  // Pending vote-escrow revenue share (RevenueRouter's staker leg, paid to lockers - see StakeSection's own
  // RevenueSharePanel, which this hook's read side is shared with), valued in USD via whichever
  // prices this component already has on hand for each asset RevenueRouter has ever routed.
  // HORIZON-denominated revenue only contributes if a real HORIZON price is available (hasPrice) - never
  // guessed - the same "unpriced contributes $0" convention vault pricing above already follows.
  // (Hook call itself now lives above the early return too.)
  // Priced against EVERY listed reserve, not two hardcoded ones: RevenueRouter routes whatever
  // genesis whitelists, so a WBTC or wstETH revenue stream was silently valued at $0 here while
  // still showing its raw amount. Decimals come from the matched reserve too - the old branch
  // called toNumber with the default 18 regardless, which would misprice any 8-decimal asset even
  // when the price itself was right.
  const revenueShareUsd = pendingRevenueByAsset.reduce((sum, { asset, amount }) => {
    const a = (asset || '').toLowerCase()
    const match = d.reserves.find((r) => r.asset?.toLowerCase() === a)
    if (match) return sum + toNumber(amount, match.decimals) * match.priceUsd
    if (a === HORIZON_ADDR && hasPrice) return sum + toNumber(amount) * horizonUsd
    return sum // unpriced contributes $0 rather than a guess - the convention vault pricing uses
  }, 0)

  // A wallet with nothing going on anywhere has nothing worth sharing - "$0.00 net in Borrow" as
  // the sole line isn't proof of profitability, it's just noise, so the card doesn't render at all
  // rather than offering an empty flex.
  const hasAnythingToShare = horizonBalance > 0 || totalEarnedHorizon > 0 ||
    earnValueUsd > 0 || userLocked > 0 || borrowNetUsd !== 0 || revenueShareUsd > 0

  // Single source of truth for this wallet's shareable stats - used both by ShareCard's "Copy
  // Stats" and the "Share on X" button (now on Your Position, not ShareCard - see PositionPanel's
  // own doc comment) so both share the exact same numbers instead of two prop lists drifting.
  const shareProps = {
    horizonBalance, horizonValueUsd, hasHorizonPrice: hasPrice,
    totalEarnedHorizon, totalEarnedUsd,
    earnValueUsd, earnAprPct: usdx ? usdx.supplyRateBps / 100 : 0,
    stakedHorizon: userLocked,
    borrowNetUsd, revenueShareUsd,
  }

  return (
    <div id="portfolio">
      <SectionHeader title="Portfolio" subtitle="Your complete position across the platform." />

      {isViewingOther && (
        <div style={{ marginBottom: 20 }}>
          <Badge color={T.textDim}>
            Viewing {truncAddr(viewAddress)} (read-only). Connect this wallet to manage it.
          </Badge>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* ── Hero: total value across every product, with Total Earned (and Locked HORIZON, when any)
             as smaller companion stats underneath - one anchor number, its secondaries subordinate,
             and the old orphaned single-card "Locked HORIZON" grid folded in here. ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <HeroFigure
            eyebrow="Total Portfolio Value"
            value={fmtUsd(totalPortfolioUsd)}
            color={T.accent}
          />
          <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap', justifyContent: 'center', marginTop: 2 }}>
            <div style={{ textAlign: 'center' }}>
              <Stat size="heading" label="Total Earned" value={fmtUsd(totalEarnedUsd)} color={T.purple} align="center" />
            </div>
            {userLocked > 0 && (
              <div style={{ textAlign: 'center' }}>
                <Stat size="heading" label="Locked HORIZON" value={fmt(userLocked, 2)} sub="earning revenue share" color={T.text} align="center" />
              </div>
            )}
          </div>
        </div>

        {/* First-run guidance: a wallet holding nothing across the whole protocol gets a short path
            to create a position, rather than a page of $0.00 panels with no next step. */}
        {isEmptyPortfolio && !isViewingOther && (
          <Panel style={{ padding: 28, textAlign: 'center' }}>
            <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>Nothing here yet.</div>
            <div style={{ marginTop: 8, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
              Put HORIZON or your assets to work, and your positions, rewards and points show up here.
            </div>
            <div style={{ marginTop: 18, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/buy" style={{ textDecoration: 'none' }}><Button variant="primary">Buy HORIZON</Button></Link>
              <Link to="/markets" style={{ textDecoration: 'none' }}><Button variant="secondary">Supply &amp; earn</Button></Link>
              <Link to="/stake" style={{ textDecoration: 'none' }}><Button variant="secondary">Lock for revenue</Button></Link>
            </div>
          </Panel>
        )}

        {/* ── Your position - lifted directly under the hero: it's the panel a returning user comes
             here to ACT on (read balances, claim rewards), so it leads rather than sitting below a
             chart and a points block. ── */}
        <PositionPanel
          d={d}
          vaultEmissions={vaultEmissions}
          user={isViewingOther ? null : user}
          horizonBalance={horizonBalance}
          horizonValueUsd={horizonValueUsd}
          hasPrice={hasPrice}
          borrowNetUsd={borrowNetUsd}
          hasMoneyMarketPosition={hasMoneyMarketPosition}
          revenueShareUsd={revenueShareUsd}
          pendingRevenueByAsset={pendingRevenueByAsset}
          onClaimed={() => {
            refetchProtocol().catch((e) => console.error('refetchProtocol after claim failed:', e))
            refetchRevenueShare().catch((e) => console.error('refetchRevenueShare after claim failed:', e))
          }}
          onShare={hasAnythingToShare ? () => shareOnX(shareProps) : null}
        />

        {/* ── Value over time ──────────────────────────────────────────── */}
        <PortfolioHistoryPanel
          series={historySeries} loading={historyLoading}
          eth={eth} horizonUsd={horizonUsd} hasPrice={hasPrice}
          liveHorizonBalance={horizonBalance}
        />

        {/* ── Vault positions - replaced the Farm Positions panel when HORIZON/USDx became a CLM
             vault. Values come from useVaultsList's own TWAP-derived pricing, the same figures the
             Vaults page shows, so the two surfaces can never disagree. ── */}
        {hasVaultPosition && (
          <Panel style={{ padding: 28 }}>
            <SectionLabel style={{ marginBottom: 14 }}>Vault Positions</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {heldVaults.map((v) => <VaultPositionRow key={v.address} v={v} emissions={vaultEmissions} />)}
            </div>
          </Panel>
        )}

        {/* ── Points + referral - a static snapshot (scripts/indexPoints.cjs), not a live
             contract read; see PointsPanel's own doc comment ── */}
        <PointsPanel viewAddress={viewAddress} connected={connected} isViewingOther={isViewingOther} />

      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Points + referral - reads scripts/indexPoints.cjs's static snapshot (public/points.json), NOT
// a live contract call, since points are never stored on-chain at all (see
// ReferralRegistry.sol's own doc comment for why). Shows whichever address Portfolio is
// currently viewing (own points, referral bonus, referee count are all public/transparent
// information, fine to show for a watched/linked address too) - the "copy your referral link"
// action specifically stays tied to the CONNECTED wallet, not the viewed address, since sharing
// someone else's link would misattribute referrals; hidden entirely while viewing another
// address, same as the Claim button.
// ─────────────────────────────────────────────────────────────────

function PointsPanel({ viewAddress, connected, isViewingOther }) {
  const { data, loading, pointsFor } = usePointsData()
  const [copied, setCopied] = useState(false)

  if (!viewAddress) return null
  if (!loading && !data) return null // no points.json published yet - nothing to show

  const p = pointsFor(viewAddress) ?? { ownPoints: 0, referralBonusPoints: 0, totalPoints: 0, referralCount: 0 }
  const referralLink = connected ? `${window.location.origin}/?ref=${connected}` : ''

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      console.error('clipboard write failed:', e)
    }
  }

  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel style={{ marginBottom: 14 }}>Points {isViewingOther ? '' : '& Referrals'}</SectionLabel>
      {loading ? (
        <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Stat size="sm" label="Total points" value={fmt(p.totalPoints, 2)} color={T.positive} />
            <Stat size="sm" label="From own activity" value={fmt(p.ownPoints, 2)} />
            <Stat size="sm" label="From referrals" value={fmt(p.referralBonusPoints, 2)} />
            <Stat size="sm" label="Addresses referred" value={String(p.referralCount)} />
          </div>
          {!isViewingOther && connected && (
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
                Share your referral link - you earn a bonus on everything they do.
              </span>
              <Button variant="ghost" onClick={copyLink} style={{ padding: '8px 16px', fontSize: T.fs.caption, borderColor: copied ? T.positive : undefined }}>
                {copied ? 'Copied!' : 'Copy referral link'}
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Total Portfolio Value (over time) - the same figure the hero above shows (HORIZON wallet balance +
// Staked HORIZON + Money Market net + Farm LP value), charted instead of a single snapshot,
// reconstructed from this wallet's own real events (see useUserPortfolioHistory's own doc
// comment). One combined line, not per-component lines - it's meant to read as "the same number
// as the hero, over time," not a breakdown chart. Every historical point values the non-USDx legs
// (ETH, HORIZON, Farm LP's HORIZON side) at the CURRENT price, not the price at that point in time -
// this app has no historical price feed, same caveat `useHistoricalStats`/`StatsMarketPanel`
// already carry for their own charts.
//
// HORIZON wallet balance is re-anchored to the live current balance, not trusted as an absolute
// number straight off the hook. useUserPortfolioHistory's own Transfer scan starts at
// LENDING_POOL_DEPLOY_BLOCK (reused across every scan there), but HorizonToken mints this wallet's
// starting balance at ITS OWN, earlier deploy step - a real pre-existing balance the scan window
// never sees, so the hook's own series assumes balance = 0 at the window start when it wasn't.
// That assumption IS safe for the money-market/staking/farm legs (this wallet couldn't have
// supplied/staked/farmed anything before those contracts existed), just not for a plain ERC20
// balance with no such guarantee. Fix: shift every point by the same constant offset so the
// series' last (current) point exactly matches the live balance this component already has -
// this keeps every real delta the scan DID observe intact, it just stops pretending the
// unobserved starting balance was zero.
// ─────────────────────────────────────────────────────────────────

function PortfolioHistoryPanel({ series, loading, eth, horizonUsd, hasPrice, liveHorizonBalance }) {
  const hasHistory = series && series.length > 1

  let totalValues = []
  if (hasHistory) {
    const horizonBalanceOffset = liveHorizonBalance - series[series.length - 1].horizonBalance
    totalValues = series.map((p) => {
      const netUsd = (p.suppliedUsdx - p.debtUsdx) + (p.suppliedEth - p.debtEth) * (eth?.priceUsd ?? 0)
      const stakedUsd = hasPrice ? p.stakedHorizon * horizonUsd : 0
      const horizonBalUsd = hasPrice ? (p.horizonBalance + horizonBalanceOffset) * horizonUsd : 0
      // The LP-farm leg that used to be summed here is gone with the farm itself. Vault holdings
      // are deliberately NOT back-filled in its place: this series is reconstructed from event
      // logs, and a vault share's historical USD value needs that vault's NAV at each past block,
      // which no event carries. Leaving it out understates history rather than inventing it - the
      // live headline figure above already counts vaults in full.
      return netUsd + stakedUsd + horizonBalUsd
    })
  }

  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel style={{ marginBottom: 14 }}>Value over time</SectionLabel>

      <div style={{ marginTop: 20 }}>
        {loading ? (
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '30px 0' }}>Loading history…</div>
        ) : !hasHistory ? (
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, padding: '30px 0' }}>
            Not enough activity on this wallet yet to chart.
          </div>
        ) : (
          <MiniLineChart
            series={series}
            lines={[{ values: totalValues, color: T.accent }]}
            height={180}
          />
        )}
      </div>
    </Panel>
  )
}

/// One held ALM vault. Deliberately static on the pending figure rather than ticking it up live
/// the way the old farm row did: vault emissions accrue against a `rewardPerShare` index whose
/// per-second rate depends on the vault's own weight AND its total supply, and this row has the
/// weight table nowhere in scope. A number that ticks at a rate this component had to guess would
/// look more precise than it is - the exact figure is one poll away regardless.
function VaultPositionRow({ v, emissions }) {
  const pending = emissions.positions.find((p) => p.address.toLowerCase() === v.address.toLowerCase())
  return (
    <div style={rowBox}>
      <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>{v.name}</div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <Stat size="sm" label="Vault shares" value={fmt(toNumber(v.userShares ?? 0n), 4)} />
        <Stat size="sm" label="Pool share" value={`${(v.userSharePct * 100).toFixed(3)}%`} />
        <Stat
          size="sm"
          label="USD value"
          value={`${v.fullyPriced ? '' : '≥'}${fmtUsd(v.userTvlUsd)}`}
          sub={v.fullyPriced ? undefined : 'one side has no USD reference'}
          color={T.positive}
        />
        <Stat size="sm" label="Pending HORIZON"
          value={fmtBalance(toNumber(pending?.pendingHorizon ?? 0n), 6)} color={T.positive} />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Your Position - one panel instead of three: Wallet Balance (a TORUS-style small bullet -
// label, big value, liquid-$-value subtext), the Money Market breakdown (per-reserve supply/
// borrow, net across both reserves), and every pending HORIZON reward summed into one number with
// one Claim All action. These three used to be separate boxes that each repeated the same
// "Money Market Net" figure - one panel reads as a single pane of glass on what this wallet
// currently has active across the protocol, instead of three overlapping summaries.
//
// Claiming spans three contracts under the hood - `claimMany` on the emissions controller (every
// supply sToken plus every vault position), `claimBorrowIncentive` on the pool per borrowed reserve
// (borrow is the one leg the controller can't read a live balance for), and `claimRevenue` on
// HorizonVoteEscrow for any pending revenue share. These are handed to `useAction` as one intent, so
// on a batch-capable wallet (EIP-5792) the whole sweep is a SINGLE confirmation; on legacy wallets
// it degrades to the sequential prompts it always was. See INTENT-UX-PLAN.md. The reason it can't
// be one on-chain call is that each claim reads the caller's own position and pays the caller - an
// aggregator contract would read its own (empty) position - so batching belongs at the wallet, not
// in a router. The wallet never needs to see the per-stream breakdown, just the one total.
//
// The headline HORIZON total ticks live via `useLiveAccrual`, same as every other pending-reward
// figure in this app - summed BEFORE ticking (pending bases summed, rates summed, one
// `useLiveAccrual` call over the totals) rather than one ticker per stream, since
// `useLiveAccrual`'s own display is a linear function of (value, rate): summing N linear tickers
// anchored at the same instant is exactly equal to one ticker over the summed value/rate, and a
// dynamic-length farm list can't drive one hook call per farm anyway (rules of hooks). Revenue
// share is NOT folded into this HORIZON figure - it's paid in ETH/USDx/HORIZON depending on what
// RevenueRouter has routed, so "HORIZON pending" isn't the right unit for it; shown as its own USD
// stat instead, right below.
//
// "Share on X" also lives here now, top-right of the header - moved off ShareCard (see that
// component's own doc comment) since this is the panel that actually shows what's being shared.
// `onShare` is null rather than the button just being disabled when there's nothing worth
// sharing yet - same "nothing to share" gate ShareCard's own visibility already uses.
// ─────────────────────────────────────────────────────────────────

function PositionPanel({
  d, vaultEmissions, user, horizonBalance, horizonValueUsd, hasPrice, borrowNetUsd, hasMoneyMarketPosition,
  revenueShareUsd, pendingRevenueByAsset, onClaimed, onShare,
}) {
  const action = useAction()

  const reservePendingBase = d.reserves.reduce((s, r) => s + r.user.pendingSupplyHorizon + r.user.pendingBorrowHorizon, 0)
  const reserveRatePerSecond = d.reserves.reduce((s, r) => {
    const supplyRate = r.totalSupplied > 0 ? toNumber(r.supplyHorizonPerSecond) * (r.user.supplyBalance / r.totalSupplied) : 0
    const borrowRate = r.totalBorrows > 0 ? toNumber(r.borrowHorizonPerSecond) * (r.user.debt / r.totalBorrows) : 0
    return s + supplyRate + borrowRate
  }, 0)
  // Vault emissions have no per-second rate to tick here: the rate depends on the per-vault weight
  // genesis sets on HorizonEmissions, which this reserve-shaped panel has nowhere in scope. Folded in
  // as a static base so the headline total is COMPLETE - a total that silently omits a real
  // claimable stream is worse than one that ticks a little less smoothly.
  const vaultPendingBase = vaultEmissions?.pendingBase ?? 0
  const totalPending = useLiveAccrual(reservePendingBase + vaultPendingBase, reserveRatePerSecond)

  const hasClaimableRevenue = pendingRevenueByAsset.some((p) => p.amount > 0n)

  async function claimAll() {
    // Unified emissions controller (entry 177): supply-side HORIZON - money-market SUPPLY (keyed by
    // each reserve's sToken) plus every vault position - is claimed from the controller in ONE
    // batch. Borrow is the only leg the controller cannot read a live balance for, so it is claimed
    // through the pool per borrowed reserve.
    const supplyKeys = [
      ...d.reserves.filter((r) => r.user.pendingSupplyHorizon > 0 && r.sTokenAddress).map((r) => r.sTokenAddress),
      ...(vaultEmissions?.positions ?? []).map((v) => v.address),
    ]
    const calls = []
    if (supplyKeys.length > 0) {
      calls.push({ address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'claimMany', args: [supplyKeys] })
    }
    for (const r of d.reserves.filter((r) => r.user.pendingBorrowHorizon > 0)) {
      calls.push({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'claimBorrowIncentive', args: [r.asset] })
    }
    if (hasClaimableRevenue) {
      calls.push({ address: ADDRESSES.HORIZON_REVENUE_DISTRIBUTOR, abi: distributorAbi, functionName: 'claimMany', args: [pendingRevenueByAsset.map((p) => p.asset)] })
    }
    if (calls.length === 0) return
    const res = await action.run({ calls, successMsg: 'All HORIZON rewards claimed.' })
    if (res) {
      vaultEmissions?.refetch?.()
      onClaimed?.()
    }
  }

  return (
    <Panel style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <SectionLabel>Your Position</SectionLabel>
        {onShare && (
          <Button variant="ghost" onClick={onShare} style={{ padding: '8px 16px', fontSize: T.fs.caption }}>
            Share on X
          </Button>
        )}
      </div>

      {/* Wallet Balance bullet */}
      <div style={rowBox}>
        <Stat
          size="heading"
          label="Wallet Balance"
          value={`${fmt(horizonBalance, 2)} HORIZON`}
          sub={hasPrice ? `${fmtUsd(horizonValueUsd)} liquid wallet value` : undefined}
          color={T.text}
        />
      </div>

      {/* Money Market breakdown - only when there's actually a position to break down */}
      {hasMoneyMarketPosition && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {d.reserves.filter((r) => r.user.supplyBalance > 0 || r.user.debt > 0).map((r) => (
            <div key={r.key} style={rowBox}>
              <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>{r.symbol}</div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                {r.user.supplyBalance > 0 && (
                  <Stat size="sm" label="Supplied" value={`${fmt(r.user.supplyBalance, 4)} ${r.symbol}`} sub={fmtUsd(r.user.supplyBalance * r.priceUsd)} color={T.positive} />
                )}
                {r.user.pendingSupplyHorizon > 0 && <Stat size="sm" label="Supply HORIZON" value={fmtBalance(r.user.pendingSupplyHorizon, 6)} color={T.positive} />}
                {r.user.debt > 0 && (
                  <Stat size="sm" label="Borrowed" value={`${fmt(r.user.debt, 4)} ${r.symbol}`} sub={fmtUsd(r.user.debt * r.priceUsd)} color={T.negative} />
                )}
                {r.user.pendingBorrowHorizon > 0 && <Stat size="sm" label="Borrow HORIZON" value={fmtBalance(r.user.pendingBorrowHorizon, 6)} color={T.positive} />}
              </div>
            </div>
          ))}
          <div style={{ ...rowBox, background: 'transparent', border: 'none', padding: '4px 0 0' }}>
            <Stat size="sm" label="Net across both markets" value={fmtUsd(borrowNetUsd)} color={borrowNetUsd >= 0 ? T.positive : T.negative} />
          </div>
        </div>
      )}

      {/* Revenue share - a separate, non-HORIZON-denominated stream from RevenueRouter's staker cut,
          claimed by the same "Claim All" action below but shown as its own USD figure since it
          isn't part of the HORIZON total */}
      {revenueShareUsd > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={rowBox}>
            <Stat size="sm" label="Revenue Share pending" value={fmtUsd(revenueShareUsd)} color={T.positive} />
          </div>
        </div>
      )}

      {/* Every pending HORIZON reward, claimed in one action */}
      <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
        <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          Every pending HORIZON reward across Earn, Borrow, and Vaults{hasClaimableRevenue ? ', plus your pending Revenue Share above' : ''}.
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ color: T.positive, fontFamily: T.fontN, fontSize: 32, fontWeight: 700 }}>
            <span className="tabnum">{fmtBalance(totalPending, 4)}</span> HORIZON
          </div>
          <Button onClick={claimAll} disabled={action.pending || (totalPending <= 0 && !hasClaimableRevenue) || !user} style={{ padding: '14px 24px', fontSize: T.fs.ui }}>
            {action.pending ? 'Working…' : 'Claim All'}
          </Button>
        </div>
        {/* Claim All sweeps supply/borrow HORIZON, vault emissions and multi-asset revenue share. On a
            batch-capable wallet that is one confirmation; otherwise the prompts come one per stream.
            The copy tells the truth for the connected wallet rather than always warning of several. */}
        {user && (totalPending > 0 || hasClaimableRevenue) && (
          <div style={{ marginTop: 10, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
            {action.atomic
              ? 'Sweeps every reward stream at once, in a single confirmation.'
              : 'Sweeps every reward stream at once - expect a few wallet confirmations.'}
          </div>
        )}
      </div>
    </Panel>
  )
}

const rowBox = {
  padding: '14px 16px', borderRadius: T.r.md, border: `1px solid ${T.border}`,
  background: 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column', gap: 10,
}
